import { createEvent, forward, restore, attach, combine } from 'effector-root'
import { uploadMediaFx } from '@/features/api/media/upload-media'
import {
  $questionData,
  setupMovingOnImageAnswerDataFx,
} from '@/pages/common/parts/tasks/moving-images-on-image-input-answer/form/moving-images-on-image-answer-form.model'
import { $audioFiles, getAudioFilesFx } from '@/pages/common/parts/audio-files/audio-files.model'
import { BaseAssignment } from '@/features/api/assignment/types/types'
import {
  MovingOnImageInput,
  MovingOnImageQuestionData,
} from '@/pages/common/parts/tasks/moving-images-on-text-input-answer/form/types'
import { DraggableImage } from '@/pages/common/parts/tasks/types'

export const uploadMedia = attach({
  effect: uploadMediaFx,
})

export const clearFields = createEvent<void>()

export const setWording = createEvent<string>()
export const $wording = restore(setWording, '').reset(clearFields)

export const setContaining = createEvent<string>()
export const $containing = restore(setContaining, '').reset(clearFields)

export const setAnswerExample = createEvent<string>()
export const $answerExample = restore(setAnswerExample, '').reset(clearFields)

export const $isFilled = combine($wording, $questionData, (wording, questionData) => {
  if (!wording) return false
  if (questionData.mainImage === '') return false
  if (!questionData.droppable.length && !questionData.inputs.length) return false
  if (
    questionData['draggable-text'].length &&
    (!questionData['draggable-text'].every((input) => !!input.text) || !questionData.inputs.length)
  )
    return false
  if (
    questionData.draggable.filter((input) => !!input.value).length !== questionData.droppable.length
  )
    return false
  if (
    !!questionData.inputs.length &&
    !questionData.inputs.every((input) => input.value.some((value) => value.value !== ''))
  )
    return false
  return true
})

export const $form = combine(
  $wording,
  $answerExample,
  $containing,
  $audioFiles,
  $questionData,
  (wording, example_answer, containing, audio, questionData) => {
    return {
      wording,
      example_answer,
      text: containing,
      question_data: {
        draggable: questionData.draggable.map(({ id, image, ratio, size }) => ({
          id,
          image,
          ratio,
          size,
        })),
        'draggable-text': questionData['draggable-text'],
        inputs: questionData.inputs
          ? questionData.inputs.map(({ id, color, position, size }) => ({
              id,
              color,
              position,
              size,
            }))
          : null,
        droppable: questionData.droppable,
        mainImage: questionData.mainImage,
        size: questionData.size,
      },
      correct_answer: {
        draggable: questionData.draggable.map(({ id, value }) => ({ id, value })),
        inputs: questionData.inputs
          ? questionData.inputs.map(({ id, value }) => ({ id, value }))
          : null,
      },
      common_list_text_answer: null,
      audio: audio.map(({ id, isLimited, limit }) => ({
        id,
        isLimited,
        limit,
      })),
    }
  }
)

export const initAssignment = createEvent<BaseAssignment>()

forward({
  from: initAssignment,
  to: [
    setWording.prepend((data) => data.wording || ''),
    setContaining.prepend((data) => data.text || ''),
    setAnswerExample.prepend((data) => data.example_answer || ''),
    setupMovingOnImageAnswerDataFx.prepend(
      ({ question_data, correct_answer }): MovingOnImageQuestionData => {
        return {
          draggable:
            (question_data.draggable &&
              question_data.draggable.map((data: DraggableImage, id: number) => ({
                ...data,
                value: correct_answer.draggable[id].value,
              }))) ||
            [],
          droppable: question_data.droppable || [],
          'draggable-text': question_data['draggable-text'] || [],
          inputs:
            (question_data.inputs &&
              question_data.inputs.map((data: MovingOnImageInput, id: number) => ({
                ...data,
                value: correct_answer.inputs[id].value,
              }))) ||
            [],
          mainImage: question_data.mainImage,
          size: question_data.size,
        }
      }
    ),
    getAudioFilesFx.prepend(({ audios }) => audios),
  ],
})
